
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/ui/date-picker-with-range';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Search, 
  Filter, 
  Download,
  Eye,
  AlertTriangle,
  Shield,
  User,
  FileText,
  Settings,
  Database
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';

interface AuditEvent {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  action: string;
  category: string;
  resource: string;
  details: any;
  ipAddress: string;
  userAgent: string;
  riskLevel: 'low' | 'medium' | 'high';
  status: 'success' | 'failure' | 'warning';
}

const AuditTrailsManager: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);

  const categories = [
    { value: 'authentication', label: 'Authentication', icon: Shield },
    { value: 'user_management', label: 'User Management', icon: User },
    { value: 'content_management', label: 'Content Management', icon: FileText },
    { value: 'system_settings', label: 'System Settings', icon: Settings },
    { value: 'data_access', label: 'Data Access', icon: Database },
    { value: 'security', label: 'Security Events', icon: AlertTriangle }
  ];

  const { data: auditEvents, isLoading } = useQuery({
    queryKey: ['audit-trails', searchTerm, categoryFilter, riskFilter, dateRange],
    queryFn: async () => {
      // Simulate comprehensive audit data
      return [
        {
          id: '1',
          timestamp: new Date().toISOString(),
          userId: 'user-1',
          userName: 'admin@example.com',
          action: 'bulk_status_update',
          category: 'content_management',
          resource: 'nodes',
          details: { nodeCount: 15, newStatus: 'published' },
          ipAddress: '192.168.1.100',
          userAgent: 'Mozilla/5.0...',
          riskLevel: 'medium' as const,
          status: 'success' as const
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          userId: 'user-2',
          userName: 'editor@example.com',
          action: 'failed_login',
          category: 'authentication',
          resource: 'auth',
          details: { reason: 'invalid_password', attempts: 3 },
          ipAddress: '192.168.1.101',
          userAgent: 'Mozilla/5.0...',
          riskLevel: 'high' as const,
          status: 'failure' as const
        },
        {
          id: '3',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          userId: 'user-3',
          userName: 'contributor@example.com',
          action: 'data_export',
          category: 'data_access',
          resource: 'user_data',
          details: { exportType: 'comprehensive', recordCount: 1250 },
          ipAddress: '192.168.1.102',
          userAgent: 'Mozilla/5.0...',
          riskLevel: 'low' as const,
          status: 'success' as const
        }
      ] as AuditEvent[];
    }
  });

  const filteredEvents = auditEvents?.filter(event => {
    const matchesSearch = 
      event.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.resource.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || event.category === categoryFilter;
    const matchesRisk = riskFilter === 'all' || event.riskLevel === riskFilter;
    
    const matchesDateRange = !dateRange?.from || (
      new Date(event.timestamp) >= dateRange.from &&
      (!dateRange.to || new Date(event.timestamp) <= dateRange.to)
    );

    return matchesSearch && matchesCategory && matchesRisk && matchesDateRange;
  }) || [];

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-white text-black';
      case 'low': return 'bg-white text-green-800';
      default: return 'bg-white text-black';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'bg-white text-green-800';
      case 'failure': return 'bg-red-100 text-red-800';
      case 'warning': return 'bg-white text-black';
      default: return 'bg-white text-black';
    }
  };

  const getCategoryIcon = (category: string) => {
    const cat = categories.find(c => c.value === category);
    return cat ? <cat.icon className="h-4 w-4" /> : <FileText className="h-4 w-4" />;
  };

  const exportAuditLog = () => {
    const csvContent = [
      ['Timestamp', 'User', 'Action', 'Category', 'Resource', 'Status', 'Risk Level', 'IP Address'].join(','),
      ...filteredEvents.map(event => [
        format(new Date(event.timestamp), 'yyyy-MM-dd HH:mm:ss'),
        event.userName,
        event.action,
        event.category,
        event.resource,
        event.status,
        event.riskLevel,
        event.ipAddress
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Comprehensive Audit Trails
            </CardTitle>
            <Button onClick={exportAuditLog} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export Audit Log
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black" />
              <Input
                placeholder="Search audit events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.value} value={category.value}>
                    <div className="flex items-center gap-2">
                      <category.icon className="h-4 w-4" />
                      {category.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All risk levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All risk levels</SelectItem>
                <SelectItem value="high">High Risk</SelectItem>
                <SelectItem value="medium">Medium Risk</SelectItem>
                <SelectItem value="low">Low Risk</SelectItem>
              </SelectContent>
            </Select>

            <DatePickerWithRange
              date={dateRange}
              onDateChange={setDateRange}
            />
          </div>

          <div className="text-sm text-black">
            Showing {filteredEvents.length} of {auditEvents?.length || 0} audit events
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-96">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-mono text-sm">
                      {format(new Date(event.timestamp), 'MMM dd, HH:mm:ss')}
                    </TableCell>
                    
                    <TableCell>
                      <div className="font-medium">{event.userName}</div>
                      <div className="text-xs text-black">{event.ipAddress}</div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="font-medium">{event.action}</div>
                      <div className="text-xs text-black">{event.resource}</div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(event.category)}
                        <span className="text-sm">{event.category}</span>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <Badge className={getStatusColor(event.status)}>
                        {event.status}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <Badge className={getRiskColor(event.riskLevel)}>
                        {event.riskLevel}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedEvent(event)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {selectedEvent && (
        <Card>
          <CardHeader>
            <CardTitle>Event Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h4 className="font-medium mb-2">Basic Information</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>Event ID:</strong> {selectedEvent.id}</div>
                  <div><strong>Timestamp:</strong> {format(new Date(selectedEvent.timestamp), 'PPpp')}</div>
                  <div><strong>User:</strong> {selectedEvent.userName}</div>
                  <div><strong>Action:</strong> {selectedEvent.action}</div>
                  <div><strong>Resource:</strong> {selectedEvent.resource}</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Technical Details</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>IP Address:</strong> {selectedEvent.ipAddress}</div>
                  <div><strong>User Agent:</strong> {selectedEvent.userAgent.substring(0, 50)}...</div>
                  <div><strong>Details:</strong></div>
                  <pre className="bg-muted p-2 rounded text-xs">
                    {JSON.stringify(selectedEvent.details, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AuditTrailsManager;
